import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "../components/Layout";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, Package, Wrench, Tag, ImageOff } from "lucide-react";

const BE = process.env.REACT_APP_BACKEND_URL;

export default function Listings() {
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ kind: "product", title: "", price: "", category: "", condition: "Good", description: "" });

  const load = () => api.get("/listings").then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title.trim()) { toast.error("Give it a title first"); return; }
    setBusy(true);
    try {
      const body = { ...form, price: form.price === "" ? null : Number(form.price) };
      if (form.kind === "service") body.condition = null;
      const { data } = await api.post("/listings", body);
      toast.success("Listing created");
      nav(`/listings/${data.id}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const fld = (k) => ({ value: form[k], onChange: (e) => setForm((f) => ({ ...f, [k]: e.target.value })) });

  return (
    <Layout>
      <div className="space-y-6" data-testid="listings-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="label-mono flex items-center gap-2"><Tag className="w-4 h-4" /> Products & Services</div>
            <h1 className="font-head font-bold text-2xl sm:text-3xl mt-1">Your listings</h1>
            <p className="text-sm mt-1" style={{ color: "var(--text-2)" }}>List what you sell, let AI sharpen it, and post it to marketplaces.</p>
          </div>
          <button onClick={() => setCreating(!creating)} className="pm-btn flex items-center gap-2 text-sm" data-testid="new-listing-btn">
            <Plus className="w-4 h-4" /> New listing
          </button>
        </div>

        {creating && (
          <div className="pm-card p-5 space-y-3" data-testid="listing-create-form">
            <div className="flex gap-2">
              {["product", "service"].map((k) => (
                <button key={k} onClick={() => setForm((f) => ({ ...f, kind: k }))} className="badge flex items-center gap-1.5"
                  data-testid={`listing-kind-${k}`}
                  style={{ background: form.kind === k ? "var(--text)" : "transparent", color: form.kind === k ? "#fff" : "var(--text)" }}>
                  {k === "product" ? <Package className="w-3.5 h-3.5" /> : <Wrench className="w-3.5 h-3.5" />} {k}
                </button>
              ))}
            </div>
            <input className="pm-input" placeholder="Title (e.g. Vintage oak coffee table)" {...fld("title")} data-testid="listing-title-input" />
            <div className="grid sm:grid-cols-2 gap-3">
              <input className="pm-input" type="number" placeholder="Price (USD)" {...fld("price")} data-testid="listing-price-input" />
              <input className="pm-input" placeholder="Category" {...fld("category")} data-testid="listing-category-input" />
            </div>
            {form.kind === "product" && (
              <select className="pm-input" {...fld("condition")} data-testid="listing-condition-input">
                {["New", "Like New", "Good", "Fair", "For parts"].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            )}
            <textarea className="pm-input" rows={3} placeholder="Description" {...fld("description")} data-testid="listing-description-input" />
            <div className="flex gap-2">
              <button onClick={create} disabled={busy} className="pm-btn text-sm" data-testid="listing-create-submit">{busy ? "Creating…" : "Create listing"}</button>
              <button onClick={() => setCreating(false)} className="pm-btn-ghost text-sm">Cancel</button>
            </div>
          </div>
        )}

        {items && items.length === 0 && !creating && (
          <div className="pm-card p-8 text-center" data-testid="listings-empty">
            <p className="text-sm" style={{ color: "var(--text-2)" }}>No listings yet. Tap "New listing" to add your first product or service.</p>
          </div>
        )}

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="listings-grid">
          {(items || []).map((it) => (
            <button key={it.id} onClick={() => nav(`/listings/${it.id}`)} className="pm-card p-0 overflow-hidden text-left flex flex-col" data-testid={`listing-card-${it.id}`}>
              <div className="w-full h-40 flex items-center justify-center" style={{ background: "var(--surface-subtle)" }}>
                {it.photos?.[0]
                  ? <img src={`${BE}${it.photos[0].url}`} alt={it.title} className="w-full h-40 object-cover" />
                  : <ImageOff className="w-8 h-8" style={{ color: "var(--text-2)" }} />}
              </div>
              <div className="p-4 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="badge" style={{ background: "var(--surface-subtle)" }}>{it.kind}</span>
                  {it.price != null && <span className="label-mono">${it.price}</span>}
                </div>
                <div className="font-semibold text-sm leading-tight">{it.title}</div>
                {it.category && <div className="label-mono mt-1">{it.category}</div>}
              </div>
            </button>
          ))}
        </div>
      </div>
    </Layout>
  );
}
